import * as React from 'react';
import { clsx } from 'clsx';
import { CustomTooltip, UIHeading4 } from '@cian/ui-kit';
import { IconFunctionalHouse16, IconToggleHeartOn12 } from '@cian/ui-kit-design-tokens/icons';

import { INewbuildingMapPin } from '../../../../types/map';
import { NewbuildingPreview } from '../NewbuildingPreview';
import * as styles from './styles.css';

const HOVER_CLOSE_DELAY = 150;
const PREVIEW_MAX_WIDTH = 384;

export interface IMapPinProps {
  pin: INewbuildingMapPin;
  isSeen?: boolean;
  onClick(pin: INewbuildingMapPin): void;
}

export const MapPin = ({ pin, isSeen, onClick }: IMapPinProps): React.JSX.Element => {
  const anchorRef = React.useRef<HTMLAnchorElement>(null);
  const closeTimerRef = React.useRef<number | null>(null);
  const [isHovered, setHovered] = React.useState(false);

  const cancelClose = React.useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const handleMouseEnter = React.useCallback(() => {
    cancelClose();
    setHovered(true);
  }, [cancelClose]);

  const handleMouseLeave = React.useCallback(() => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => setHovered(false), HOVER_CLOSE_DELAY);
  }, [cancelClose]);

  React.useEffect(() => cancelClose, [cancelClose]);

  const handleClick = React.useCallback(() => {
    onClick(pin);
  }, [onClick, pin]);

  const isSeenOnly = Boolean(isSeen) && !isHovered;

  return (
    <div
      className={clsx(
        styles['wrapper'],
        isHovered && styles['wrapper--hovered'],
        isSeenOnly && styles['wrapper--seen'],
      )}
    >
      <a
        ref={anchorRef}
        className={clsx(
          styles['balloon'],
          isHovered && styles['balloon--hovered'],
          isSeenOnly && styles['balloon--seen'],
        )}
        data-testid="newbuildings-map-pin"
        href={pin.newbuilding.fullUrl}
        rel="noreferrer"
        target="_blank"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className={styles['icon']}>
          <IconFunctionalHouse16 color={isHovered ? 'icon-positive-default' : 'icon-inverted-default'} />
        </span>
        <span className={styles['title']}>
          <UIHeading4 color={isHovered ? 'text-inverted-default' : isSeenOnly ? 'text-secondary-default' : undefined}>
            {pin.title}
          </UIHeading4>
        </span>
        {pin.newbuilding.isFavorite && (
          <span className={styles['favorite']} data-testid="newbuildings-map-pin-favorite">
            <IconToggleHeartOn12 color={isHovered ? 'icon-inverted-default' : 'icon-negative-default'} />
          </span>
        )}
      </a>
      <span className={styles['point']} />
      <CustomTooltip
        anchorRef={anchorRef}
        arrow={false}
        content={<NewbuildingPreview pin={pin} />}
        maxWidth={PREVIEW_MAX_WIDTH}
        open={isHovered}
        placement="bottom"
        withoutPadding
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    </div>
  );
};
